import { parseSearchFiltersToUrl } from '~/apps/pu-links/infrastructure/searchUrlHelpers'
import { getTransformObject } from '~/common/photos/transforms'
import { getLangFallbackUrl } from '~/lang/utils/langfetch'
import { getWKTPointFromGeoPoint } from '~/utility/geo/GeoLocation'
import { getPointFromStringOrDefault } from '~/utility/geo/latLng'
import { keysToCamel } from '~/utility/toCamel'
import { Logger, useGtmLogRequest } from '../../../composables/useGtmLogRequest'
import {
  CampsiteSearchResult,
  Photo,
  PhotoTransforms,
  SearchCampsitesResults,
  SearchFilters,
  campsiteSearchResultSchema,
} from '../schemas'

interface SearchResponse {
  results: any[]
  page: number
  num_pages: number
  count: number
  sort?: string
  sort_field?: string
  sort_options?: any
  category?: string
  facet_models?: any
  bookable_count: number
  facet_category: Record<string, number>
  facet_keywords: Record<string, number>
  facet_hierarchy: Record<string, number>
  facet_full_bookable_category?: Record<string, number>
  title?: string
  heading?: string
  description?: string
  hierarchy_trail?: any
  hierarchy_name?: string
  hierarchy?: string
  path?: string
  point?: any
  within?: any
  page_part?: string
  faq?: any
  breadcrumbs?: any[]
  engagement_facets?: any
  engagement_destinations?: any
  engagement_narrow_links?: any
  lead_price?: any
  seo_redirect?: string
  pitchtype_facets?: string[]
  max_price?: number
  price_histogram?: any
}

export class FetchCampsiteSearchService {
  constructor(private fetchOptions?: any) {}

  async search(
    langCode: string,
    filters: SearchFilters,
    logger?: Logger,
    searchUrl?: string,
    fetchOptions?: any,
  ): Promise<SearchCampsitesResults> {
    const params = new URLSearchParams()
    for (const param of parseSearchFiltersToUrl(filters)) {
      params.append(param.filterName, param.filterValue)
    }
    if (params.get('bounds')) {
      // remove bounds if path
      if (params.get('path')) {
        params.delete('bounds')
      } else {
        // only full bookable if bounds and no path
        params.append('bookable', '1')
      }
    }
    if (searchUrl) {
      params.append('search_url', searchUrl)
    }
    if (filters.facet_counts_only) {
      params.append('facet_counts_only', filters.facet_counts_only)
    }

    let gtmLogRequest
    if (logger) {
      gtmLogRequest = useGtmLogRequest(
        'FetchCampsiteSearchService-search',
        logger,
      )
      gtmLogRequest.markStart()
    }

    const url = getLangFallbackUrl(
      langCode,
      `/search2/postgres-nonprice/?${params.toString()}`,
    )
    const response = (await $fetch<SearchResponse>(url, {
      ...this.fetchOptions,
      ...fetchOptions,
    })) as SearchResponse

    if (logger && gtmLogRequest) {
      gtmLogRequest.markEnd()
    }

    return {
      campsites: this.formatResults(keysToCamel(response.results)),
      pagination: {
        currentPage: Number(response.page),
        totalPages: response.num_pages,
        totalCount: response.count,
      },
      title: response.title,
      heading: response.heading,
      description: response.description,
      hierarchyTrail: response.hierarchy_trail,
      hierarchyName: response.hierarchy_name,
      hierarchy: response.hierarchy,
      path: response.path,
      point: response.point,
      within: response.within,
      sortOption: response.sort || response.sort_field,
      sortOptions: response.sort_options,
      pagePart: response.page_part,
      faq: response.faq,
      breadcrumbs:
        response.breadcrumbs?.map((breadcrumb: any) =>
          keysToCamel(breadcrumb),
        ) || [],
      engagementFacets: response.engagement_facets,
      engagementDestinations: response.engagement_destinations,
      engagementNarrowLinks: response.engagement_narrow_links,
      leadPrice: response.lead_price,
      seoRedirect: response.seo_redirect,
      pitchtypeFacets: response.pitchtype_facets,
      maxPrice: response.max_price,
      priceHistogram: response.price_histogram,
      ...this.formatNonPriceResultsCounts(response),
      ...keysToCamel(response, [
        'facet_hierarchy',
        'facet_keywords',
        'bookable_facet_hierarchy',
      ]),
      category: response.category,
      facetModels: response.facet_models,
      facetFullBookableCategory: response.facet_full_bookable_category,
    }
  }

  private formatResults(results: any[]): CampsiteSearchResult[] {
    return results.map((campsite) =>
      campsiteSearchResultSchema.parse({
        ...campsite,
        id: campsite.id.toString(),
        availableToSearch:
          typeof campsite.availableToSearch === 'undefined'
            ? campsite.bookable
            : campsite.availableToSearch,
        status: campsite.bookable ? 'bookable' : 'free',
        hierarchyPath: campsite.path,
        hierarchyStringShort: campsite.hierarchyTextShort,
        hierarchyStringShortCountry: campsite.hierarchyTextShortCountry,
        primaryPhoto: campsite.primaryPhoto
          ? this.mapPrimaryPhoto(campsite.primaryPhoto)
          : undefined,
        schemaPhoto: campsite.schemaPhoto
          ? this.mapPrimaryPhoto(campsite.schemaPhoto)
          : undefined,
        rating: Number(campsite.rating),
        rating10: campsite.rating * 2,
        leadPrice: campsite.cheapestLeadPrice
          ? campsite.cheapestLeadPrice[0]
          : undefined,
        leadPriceNights: campsite.cheapestLeadPrice
          ? campsite.cheapestLeadPrice[1]
          : undefined,
        leadPriceAdults: campsite.cheapestLeadPrice
          ? campsite.cheapestLeadPrice[2] || Number(2)
          : undefined,
        point: campsite.point
          ? getWKTPointFromGeoPoint(getPointFromStringOrDefault(campsite.point))
          : undefined,
        nutshells: campsite.nutshells || [],
        firstAvailableDate: campsite.firstAvailableDate || undefined,
        pitchtypes: this.mapPitchtypes(campsite),
        address1: campsite.address1 || undefined,
        address2: campsite.address2 || undefined,
        postcode: campsite.postcode || undefined,
        countryIso: campsite.countryIso || undefined,
      }),
    )
  }

  private mapPitchtypes(campsite) {
    const pitchtypes = campsite.pitchtypes
    return pitchtypes.map((pitchtype) => {
      const leadPrice = pitchtype.leadPrice
        ? {
            currency: campsite.currency,
            amount: pitchtype.leadPrice,
            amountMinor: parseInt((pitchtype.leadPrice * 100).toFixed(0)),
          }
        : null
      const leadPriceWithExcluded = pitchtype.leadPriceWithExcluded
        ? {
            currency: campsite.currency,
            amount: pitchtype.leadPriceWithExcluded,
            amountMinor: parseInt(
              (pitchtype.leadPriceWithExcluded * 100).toFixed(0),
            ),
          }
        : null

      let excludedPrice: {
        currency: string
        amount: number
        amountMinor: number
      } | null = null
      if (
        leadPriceWithExcluded &&
        leadPrice &&
        leadPrice.amount !== leadPriceWithExcluded.amount
      ) {
        excludedPrice = {
          currency: campsite.currency,
          amount: leadPriceWithExcluded.amount - leadPrice.amount,
          amountMinor:
            leadPriceWithExcluded.amountMinor - leadPrice.amountMinor,
        }
      }

      return {
        ...pitchtype,
        categories: pitchtype.categories.map((category) => ({
          ...category,
          id: category.id.toString(),
        })),
        leadPrice,
        leadPriceWithExcluded,
        excludedPrice,
      }
    })
  }

  private mapPrimaryPhoto(photo: Photo) {
    return {
      caption: photo.caption,
      url: {
        ...getTransformObject<Omit<PhotoTransforms, 'masterImage'>>(
          'campsitePrimaryPhotoMaster',
          photo.url.masterImage,
        ),
        masterImage: photo.url.masterImage,
      },
    }
  }

  private formatNonPriceResultsCounts(results: {
    bookable_count: number
    facet_category: Record<string, number>
    facet_keywords: Record<string, number>
    facet_hierarchy: Record<string, number>
  }) {
    return {
      bookableCount: results.bookable_count,
      categoriesCounts: results.facet_category,
      hierarchyCounts: this.getHierarchiesFromSearchResults(results),
      keywordsCounts: results.facet_keywords,
    }
  }

  private getHierarchiesFromSearchResults(results: {
    facet_hierarchy: Record<string, number>
  }) {
    const hierarchies = {}
    const hierarchiesToCover = ['facet_hierarchy']
    for (const hierarchy of hierarchiesToCover) {
      for (const [key, value] of Object.entries(results[hierarchy] || {})) {
        hierarchies[key] = value
      }
    }
    return hierarchies
  }
}
